import { useCallback, useEffect, useRef, useState } from 'react';

// Sub-pixel rounding leaves a fractional gap at the extremes, which would otherwise
// keep the end indicator visible on a fully scrolled element.
const SCROLL_EPSILON = 1;

// A page step keeps a sliver of the previous content in view for continuity.
const PAGE_FRACTION = 0.85;
const MIN_SCROLL_STEP = 120;

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

export function snapToChildEdge(
    el: HTMLElement,
    rawTarget: number,
    direction: -1 | 1,
): number {
    const max = Math.max(el.scrollWidth - el.clientWidth, 0);
    const padding =
        parseFloat(getComputedStyle(el).scrollPaddingInlineStart) || 0;
    const trackLeft = el.getBoundingClientRect().left;
    const candidates = Array.from(el.children)
        .filter((child): child is HTMLElement => child instanceof HTMLElement)
        .map((child) =>
            clamp(
                child.getBoundingClientRect().left - trackLeft + el.scrollLeft - padding,
                0,
                max,
            ),
        );

    if (candidates.length === 0) {
        return rawTarget;
    }

    const directionalCandidates = candidates.filter((candidate) =>
        direction === 1
            ? candidate > el.scrollLeft + SCROLL_EPSILON
            : candidate < el.scrollLeft - SCROLL_EPSILON,
    );

    if (directionalCandidates.length === 0) {
        return rawTarget;
    }

    return directionalCandidates.reduce((closest, candidate) =>
        Math.abs(candidate - rawTarget) < Math.abs(closest - rawTarget) ? candidate : closest,
    );
}

export interface ScrollOverflow<T extends HTMLElement = HTMLDivElement> {
    /** Attach to the horizontally scrolling element. */
    scrollRef: React.RefObject<T | null>;
    /** True while content is hidden off the left edge. */
    canScrollStart: boolean;
    /** True while content is hidden off the right edge. */
    canScrollEnd: boolean;
    /** Scroll about one viewport: -1 toward the start, 1 toward the end. */
    scrollByPage: (direction: -1 | 1) => void;
}

/**
 * Tracks whether a horizontal scroller has content hidden past either edge, so
 * callers can render edge fades and arrow buttons only when they mean something.
 *
 * Re-measures on scroll, on resize of the element, and on DOM changes within it,
 * keeping the indicators correct as children are added, removed, or re-render.
 */
export function useScrollOverflow<T extends HTMLElement = HTMLDivElement>(): ScrollOverflow<T> {
    const scrollRef = useRef<T>(null);
    const [canScrollStart, setCanScrollStart] = useState(false);
    const [canScrollEnd, setCanScrollEnd] = useState(false);

    const measure = useCallback((): void => {
        const el = scrollRef.current;
        if (!el) {
            return;
        }

        setCanScrollStart(el.scrollLeft > SCROLL_EPSILON);
        setCanScrollEnd(el.scrollWidth - el.clientWidth - el.scrollLeft > SCROLL_EPSILON);
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) {
            return;
        }

        measure();
        el.addEventListener('scroll', measure, { passive: true });

        const resizeObserver =
            typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        resizeObserver?.observe(el);

        const mutationObserver =
            typeof MutationObserver !== 'undefined' ? new MutationObserver(measure) : null;
        mutationObserver?.observe(el, {
            childList: true,
            subtree: true,
            characterData: true,
        });

        return () => {
            el.removeEventListener('scroll', measure);
            resizeObserver?.disconnect();
            mutationObserver?.disconnect();
        };
    }, [measure]);

    const scrollByPage = useCallback((direction: -1 | 1): void => {
        const el = scrollRef.current;
        if (!el) {
            return;
        }

        const max = el.scrollWidth - el.clientWidth;
        const step = Math.max(el.clientWidth * PAGE_FRACTION, MIN_SCROLL_STEP);
        const rawTarget = clamp(el.scrollLeft + direction * step, 0, max);
        const left = snapToChildEdge(el, rawTarget, direction);
        const prefersReducedMotion =
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (typeof el.scrollTo === 'function') {
            el.scrollTo({ left, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
            return;
        }

        el.scrollLeft = left;
    }, []);

    return { scrollRef, canScrollStart, canScrollEnd, scrollByPage };
}
